import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A1 總覽（`GET /api/admin/overview`，見 docs/DESIGN-admin.md §3 A1／§4.3-1）。
 *
 * 這一頁只做「呼叫聚合 endpoint 再排版」，**不自己算數字**：KPI、近 7 日、alerts 全部照
 * `AdminOverview` 給什麼顯示什麼（後端才是唯一真相，DESIGN-admin §7 最後一段）。
 * 前端唯一自己算的是長條的**相對長度**（純畫圖用）與時間／日期字串的格式。
 *
 * 空狀態是硬需求，不是加分項：
 * - `client` 沒傳、`client.overview` 不存在、或它回 `undefined`／形狀不對 → 顯示「尚未接線」
 *   的引導，**不**白畫面、**不**丟紅錯（Phase 0 的 client 只有 mock 有 `overview`）。
 * - 數字 `null` → `—`；`0` → `0`，兩者不可混用（§3 共同規則）。
 * - 那天沒跑 → `—`，不是 0（否則「沒跑」會被讀成「跑了全失敗」）。
 *
 * 資料怎麼進來：這個檔**只吃 props**，不自己 fetch、不 import 宿主的 client
 * （`packages/console-ui` 不可以依賴 `apps/console`）。`AdminShell` 現在是 `<OverviewPage />`，
 * 等外殼把 `client`／`onNavigate` 傳下來就會自動載入；在那之前停在「尚未接線」引導狀態，
 * 版面仍然可讀、可驗。
 *
 * 樣式：全部吃 `--ak-*` token 與既有 `cui-*` class，沒有新色票、新字型、新套件。
 * 響應式規則寫在本檔的 scoped CSS（`a1-` 前綴）裡，因為這一輪不准改 `styles.css`：
 * 手機 KPI 2×2、圖表改成橫向文字列表（不畫長條）、無水平捲動；桌機 ≥900px 四欄同列。
 */
import { useMemo } from 'react';
import { useSystemSnapshot } from '../../hooks/useSystemSnapshot';
/** 分頁中文名（tooltip 用，跟 DESIGN-admin §2.2 一致）。 */
const TAB_LABEL = {
    overview: '總覽',
    accounts: '帳號池',
    sessions: '工作階段',
    ops: '任務與系統',
};
/* ── 純函式（可單獨驗，不必渲染） ---------------------------------------- */
/** 數字 → 字串：`null`／`undefined`／NaN 一律 `—`；`0` 就是 `0`。 */
export function formatCount(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return '—';
    return String(value);
}
const clockFormat = new Intl.DateTimeFormat('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false });
const clockSecondFormat = new Intl.DateTimeFormat('zh-TW', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
});
const dateTimeFormat = new Intl.DateTimeFormat('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
/**
 * §3 共同規則：今天只顯示 `HH:mm`、跨日 `MM/DD HH:mm`；沒有時間就 `—`。
 *
 * `ts <= 0` 一律當「沒有時間」：worker 的告警本來就帶 `at: null`（例如「帳號被站方拒絕」），
 * 經過 `asNum()` 的 fallback 會變成 `0`；後端告警沒帶 `at` 時 adapter 也會收斂成 0。
 * 直接餵 `new Date(0)` 會畫成 1970／「01/01 00:00」，看起來像「有個很久以前的事件」，
 * 比 `—` 更誤導（§3：空與 0 不可混用）。這個時間戳不可能是真實事件，所以在這裡當缺值處理。
 */
export function formatWhen(ts, now = Date.now()) {
    if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0)
        return '—';
    const date = new Date(ts);
    const today = new Date(now);
    const sameDay = date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();
    return (sameDay ? clockFormat : dateTimeFormat).format(date);
}
/**
 * 四張 KPI 卡（§3 A1 區塊 1）：只投影，算術都在後端。
 *
 * 例外是「未讀總數」：它跟側欄「收件匣」徽章必須是同一份資料（同一個時刻、同一個算式），
 * 否則同一畫面會出現兩個不一樣的未讀數（見 `LiveUnread` 的說明）。傳了 `unreadLive`
 * 就用它；沒傳才退回 overview 快照，並在說明行標「快照」讓使用者知道這是舊的。
 */
export function buildKpis(data, note, unreadLive) {
    const accounts = data?.accounts;
    const sessions = data?.sessions;
    const has = data !== null;
    const live = unreadLive ?? null;
    return [
        {
            key: 'accounts-total',
            label: '帳號總數',
            value: formatCount(accounts?.total),
            hint: data
                ? `ek21 ${formatCount(accounts?.byService.ek21)} · UT ${formatCount(accounts?.byService.ut)}`
                : note,
            tab: null,
            href: '#/accounts',
            pending: !has,
            live: false,
        },
        {
            key: 'accounts-usable',
            label: '可用帳號',
            value: formatCount(accounts?.usable),
            hint: data
                ? `未用過 ${formatCount(accounts?.neverUsed)} · 被拒 ${formatCount(accounts?.rejected)}（皆含在總數 ${formatCount(accounts?.total)} 內）`
                : note,
            tab: null,
            href: '#/accounts',
            pending: !has,
            live: false,
        },
        {
            key: 'sessions-online',
            label: '在線 session',
            value: formatCount(sessions?.online),
            hint: data ? `休眠 ${formatCount(sessions?.dormant)} · 共 ${formatCount(sessions?.total)}` : note,
            tab: 'sessions',
            pending: !has,
            live: false,
        },
        {
            key: 'unread-total',
            label: '未讀總數',
            // 有即時來源就用它：跟左側「收件匣」徽章同一份 conversations，同一個時刻必相同。
            value: live ? (live.loading && live.sessions === 0 ? '—' : formatCount(live.total)) : formatCount(sessions?.unreadTotal),
            hint: live
                ? live.loading && live.sessions === 0
                    ? '載入中…'
                    : `即時 · 與左側「收件匣」同一份清單（${formatCount(live.sessions)} 個 session）`
                : data
                    ? `快照 · session 共 ${formatCount(sessions?.total)}（與左側徽章更新時點可能不同）`
                    : note,
            tab: 'sessions',
            pending: live ? live.loading && live.sessions === 0 : !has,
            live: live !== null,
        },
    ];
}
/** 只取後端給的最後 7 天；**不補**沒回的日子、也不把 null 洗成 0。 */
export function buildTrend(data, maxDays = 7) {
    const days = data?.registration.last7d ?? [];
    const recent = days.slice(Math.max(0, days.length - maxDays));
    let peak = 0;
    for (const day of recent) {
        for (const value of [day.ek21, day.ut, day.failed]) {
            if (typeof value === 'number' && Number.isFinite(value) && value > peak)
                peak = value;
        }
    }
    const ratios = (value) => {
        if (typeof value !== 'number' || !Number.isFinite(value))
            return { ratio: 0, missing: true };
        if (peak <= 0)
            return { ratio: 0, missing: false };
        return { ratio: Math.min(1, Math.max(0, value / peak)), missing: false };
    };
    return recent.map((day) => {
        const metrics = [
            { label: 'ek21', value: formatCount(day.ek21), tone: 'ek21', ...ratios(day.ek21) },
            { label: 'ut', value: formatCount(day.ut), tone: 'ut', ...ratios(day.ut) },
            { label: '失敗', value: formatCount(day.failed), tone: 'failed', ...ratios(day.failed) },
        ];
        return {
            date: day.date,
            label: day.date.length >= 10 ? `${day.date.slice(5, 7)}/${day.date.slice(8, 10)}` : day.date,
            metrics,
            empty: metrics.every((metric) => metric.missing),
        };
    });
}
const ALERT_LIMIT = 5;
const LEVEL_LABEL = {
    info: '提示',
    warn: '注意',
    danger: '嚴重',
};
/**
 * 告警 → 分頁（§3 A1：點一筆要切到對應分頁並帶篩選）。
 * 未知 code 一律丟 A4：那裡有 run／relay／站方原始狀態，比亂猜一頁好。
 */
export function alertTarget(code) {
    switch (code) {
        // 被拒的帳號在 `#/accounts` 那一頁（篩選由該頁自己處理），不是管理分頁。
        case 'accounts_rejected':
            return { tab: null, href: '#/accounts' };
        case 'zombie_session':
        case 'session_stale':
            return { tab: 'sessions' };
        case 'last_run_all_failed':
        case 'register_throttled':
        case 'relay_unreachable':
        case 'site_unreachable':
        case 'scheduler_failed':
            return { tab: 'ops' };
        default:
            return { tab: 'ops' };
    }
}
/** 告警最多 5 筆；`level`／`message` 直接照後端，前端不重判嚴重度。 */
export function buildAlerts(data, now = Date.now()) {
    const all = data?.alerts ?? [];
    const items = all.slice(0, ALERT_LIMIT).map((alert) => {
        const target = alertTarget(alert.code);
        return {
            code: alert.code,
            level: alert.level,
            levelLabel: LEVEL_LABEL[alert.level] ?? alert.level,
            message: alert.message,
            when: formatWhen(alert.at, now),
            tab: target.tab,
            ...(target.href ? { href: target.href } : {}),
            ...(target.filter ? { filter: target.filter } : {}),
        };
    });
    return { items, hidden: Math.max(0, all.length - ALERT_LIMIT) };
}
function alertDotClass(level) {
    if (level === 'danger')
        return 'cui-health-dot cui-health-dot--down';
    if (level === 'warn')
        return 'cui-health-dot cui-health-dot--limited';
    return 'cui-health-dot cui-health-dot--idle';
}
/* ── 樣式（scoped：只影響 a1-*，不動 styles.css） ------------------------ */
const STYLES = `
.a1-body { display: flex; flex-direction: column; gap: 10px; min-height: 0; padding: 12px; overflow-x: hidden; }
.a1-kpis { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.a1-kpi {
  display: flex; flex-direction: column; gap: 3px; min-width: 0; padding: 9px 11px;
  text-align: left; font: inherit; color: inherit; cursor: pointer;
  background: var(--ak-color-card); border: 1px solid var(--ak-color-border);
  border-radius: var(--ak-radius-md); transition: border-color 120ms ease, background 120ms ease;
}
.a1-kpi:hover {
  border-color: color-mix(in srgb, var(--ak-color-primary) 45%, var(--ak-color-border));
  background: color-mix(in srgb, var(--ak-color-primary) 6%, var(--ak-color-card));
}
.a1-kpi:focus-visible { outline: 1px solid var(--ak-color-primary); outline-offset: 1px; }
.a1-kpi-top { display: flex; align-items: baseline; gap: 6px; min-width: 0; }
.a1-kpi-label {
  font-family: var(--ak-font-mono); font-size: 10px; letter-spacing: var(--ak-tracking-eyebrow);
  text-transform: uppercase; color: var(--ak-color-text-tertiary);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.a1-kpi-go {
  margin-left: auto; flex: none; font-family: var(--ak-font-mono); font-size: 9px;
  color: var(--ak-color-primary); opacity: 0; transition: opacity 120ms ease;
}
.a1-kpi:hover .a1-kpi-go, .a1-kpi:focus-visible .a1-kpi-go { opacity: 1; }
.a1-kpi-value {
  font-family: var(--ak-font-mono); font-size: 26px; line-height: 1.15;
  font-variant-numeric: tabular-nums; color: var(--ak-color-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.a1-kpi-value.is-pending { color: var(--ak-color-text-tertiary); font-size: 20px; }
.a1-kpi-hint {
  font-size: 10.5px; line-height: 1.35; color: var(--ak-color-text-tertiary);
  overflow-wrap: anywhere;
}
.a1-card {
  display: flex; flex-direction: column; gap: 6px; min-width: 0; padding: 10px 12px;
  background: var(--ak-color-card); border: 1px solid var(--ak-color-border);
  border-radius: var(--ak-radius-md);
}
.a1-card-head { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; min-width: 0; }
.a1-card-title {
  font-family: var(--ak-font-mono); font-size: 10px; letter-spacing: var(--ak-tracking-eyebrow);
  text-transform: uppercase; color: var(--ak-color-text-tertiary);
}
.a1-card-meta { font-family: var(--ak-font-mono); font-size: 10px; color: var(--ak-color-text-tertiary); }
.a1-card-note { margin-left: auto; font-size: 10px; color: var(--ak-color-text-tertiary); }
.a1-day {
  display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 8px; align-items: center;
  padding: 5px 0; border-top: 1px solid color-mix(in srgb, var(--ak-color-border) 70%, transparent);
}
.a1-day:hover { background: color-mix(in srgb, var(--ak-color-text) 2.5%, transparent); }
.a1-day-date { font-family: var(--ak-font-mono); font-size: 10.5px; color: var(--ak-color-text-tertiary); }
.a1-day.is-empty .a1-day-date { opacity: 0.6; }
.a1-metrics { display: flex; align-items: center; gap: 4px 12px; min-width: 0; flex-wrap: wrap; }
.a1-metric { display: flex; align-items: center; gap: 5px; min-width: 0; }
.a1-metric-key {
  font-family: var(--ak-font-mono); font-size: 9.5px; letter-spacing: 0.04em;
  color: var(--ak-color-text-tertiary); flex: none;
}
.a1-metric-num {
  font-family: var(--ak-font-mono); font-size: 11.5px; font-variant-numeric: tabular-nums;
  color: var(--ak-color-text); flex: none;
}
.a1-metric[data-null="true"] .a1-metric-num { color: var(--ak-color-text-tertiary); }
.a1-metric-bar {
  display: none; position: relative; flex: 1 1 48px; min-width: 22px; height: 4px;
  background: color-mix(in srgb, var(--ak-color-text) 9%, transparent);
}
.a1-bar-ek21 { background: var(--ak-color-accent-secondary); }
.a1-bar-ut { background: var(--ak-color-pm, var(--ak-color-warning)); }
.a1-bar-failed { background: var(--ak-color-danger); }
.a1-alerts { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.a1-alert {
  display: grid; grid-template-columns: 7px minmax(0, 1fr) auto; gap: 4px 8px; align-items: start;
  width: 100%; min-width: 0; padding: 6px 8px; text-align: left; font: inherit; color: inherit;
  cursor: pointer; background: transparent; border: 1px solid transparent;
  border-radius: var(--ak-radius-md);
}
.a1-alert:hover { border-color: var(--ak-color-border); background: color-mix(in srgb, var(--ak-color-text) 3%, transparent); }
.a1-alert:focus-visible { outline: 1px solid var(--ak-color-primary); outline-offset: -1px; }
.a1-alert-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.a1-alert-level {
  font-family: var(--ak-font-mono); font-size: 9px; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--ak-color-text-tertiary);
}
.a1-alert-level.is-danger { color: var(--ak-color-danger); }
.a1-alert-level.is-warn { color: var(--ak-color-warning); }
.a1-alert-msg { font-size: 12px; line-height: 1.4; color: var(--ak-color-text); overflow-wrap: anywhere; }
.a1-alert-when {
  font-family: var(--ak-font-mono); font-size: 10px; color: var(--ak-color-text-tertiary);
  white-space: nowrap; flex: none; padding-top: 1px;
}
.a1-empty { margin: 0; padding: 6px 2px; font-size: 11.5px; line-height: 1.5; color: var(--ak-color-text-tertiary); }
.a1-state {
  display: flex; flex-direction: column; gap: 6px; min-width: 0; padding: 10px 12px;
  border: 1px dashed var(--ak-color-border); border-radius: var(--ak-radius-md);
  background: color-mix(in srgb, var(--ak-color-text) 2%, transparent);
}
.a1-state-title {
  font-family: var(--ak-font-mono); font-size: 10px; letter-spacing: var(--ak-tracking-eyebrow);
  text-transform: uppercase; color: var(--ak-color-text-tertiary);
}
.a1-state-body { margin: 0; font-size: 11.5px; line-height: 1.55; color: var(--ak-color-text-tertiary); overflow-wrap: anywhere; }
.a1-state[data-phase="error"] { border-style: solid; border-color: color-mix(in srgb, var(--ak-color-danger) 45%, var(--ak-color-border)); }
.a1-state[data-phase="error"] .a1-state-title { color: var(--ak-color-danger); }
.a1-state-action { align-self: flex-start; }
.a1-foot { margin: 0; font-size: 10px; line-height: 1.5; color: var(--ak-color-text-tertiary); }
@media (min-width: 900px) {
  .a1-body { gap: 12px; padding: 14px; }
  .a1-kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .a1-kpi-value { font-size: 30px; }
  .a1-day { grid-template-columns: 56px minmax(0, 1fr); }
  .a1-metric { flex: 1 1 110px; }
  .a1-metric-bar { display: block; flex: 1 1 auto; }
  .a1-alert { grid-template-columns: 7px minmax(0, 1fr) auto; align-items: center; }
}
`;
/* ── 元件 --------------------------------------------------------------- */
export function OverviewPage({ client, onNavigate, unreadLive } = {}) {
    const { data, phase, error, refresh } = useSystemSnapshot(client ?? null);
    /**
     * 使用者面向的「沒有資料」說法：**不**提 client／overview()／是哪一層沒傳。
     * 沒有資料時數字一律 `—`（見 `formatCount`），不畫成 0。
     */
    const note = phase === 'loading' ? '載入中…' : phase === 'error' ? '讀取失敗' : '暫時沒有接通';
    const kpis = useMemo(() => buildKpis(data, note, unreadLive ?? null), [data, note, unreadLive]);
    const trend = useMemo(() => buildTrend(data), [data]);
    const alerts = useMemo(() => buildAlerts(data), [data]);
    const refreshedAt = data ? clockSecondFormat.format(new Date(data.generatedAt)) : null;
    /** 標題右側只放「更新時間」；沒有資料時是 `—`，不再重複一次空狀態文案。 */
    const statusText = refreshedAt ? `更新 ${refreshedAt}` : phase === 'loading' ? '載入中…' : '—';
    /** §6.2-6：池子 0 顆要給引導文字，不是空白表格。 */
    const emptyPool = data !== null && data.accounts.total === 0;
    const today = data?.registration.today;
    /** 切管理分頁交給外殼；去新頁自己改 hash（外殼不用認識 `#/accounts`）。 */
    const go = (target) => {
        if (target.href) {
            window.location.hash = target.href;
            return;
        }
        if (target.tab)
            onNavigate?.(target.tab, target.filter);
    };
    /** 角落「→ 帳號」那種字：管理分頁查 `TAB_LABEL`，新頁給固定名。 */
    const goLabel = (target) => target.tab ? TAB_LABEL[target.tab] : '帳號';
    return (_jsxs("section", { className: "cui-panel", "data-testid": "admin-page-overview", "aria-label": "\u7E3D\u89BD", "data-phase": phase, children: [_jsx("style", { children: STYLES }), _jsxs("header", { className: "cui-panel-head", children: [_jsx("h2", { className: "cui-panel-title", children: "\u7E3D\u89BD" }), _jsx("span", { className: "cui-count", "data-testid": "admin-overview-updated", "aria-live": "polite", children: statusText }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: refresh, disabled: phase === 'loading', "data-testid": "admin-overview-refresh", children: phase === 'loading' ? '讀取中' : '重新整理' })] }), _jsxs("div", { className: "a1-body", children: [phase === 'unavailable' || phase === 'error' ? (_jsxs("div", { className: "a1-state", "data-phase": phase, "data-testid": "admin-overview-state", role: phase === 'error' ? 'alert' : 'status', children: [_jsx("span", { className: "a1-state-title", children: phase === 'error' ? '讀不到總覽資料' : '管理資料暫時沒有接通' }), _jsx("p", { className: "a1-state-body", children: phase === 'error'
                                    ? `讀取失敗：${error ?? '未知原因'}。數字先顯示 —，請稍後重新整理。`
                                    : '數字先顯示 —，等連線恢復後會自動更新。你可以按右上「重新整理」再試一次。' }), phase === 'error' ? (_jsx("button", { type: "button", className: "cui-btn cui-btn--sm a1-state-action", onClick: refresh, "data-testid": "admin-overview-retry", children: "\u91CD\u8A66" })) : null] })) : null, emptyPool ? (_jsxs("div", { className: "a1-state", "data-phase": "empty", "data-testid": "admin-overview-empty-pool", role: "status", children: [_jsx("span", { className: "a1-state-title", children: "\u5E33\u865F\u6C60\u662F\u7A7A\u7684" }), _jsx("p", { className: "a1-state-body", children: "\u76EE\u524D 0 \u9846\u5E33\u865F\uFF1A\u5148\u5230\u300C\u5E33\u865F\u6C60\u300D\u6309\u4E00\u6B21\u300C\u8A3B\u518A\u4E00\u6279\u300D\uFF08\u4E00\u6B21 1\u20133 \u9846\uFF0C\u5169\u6279\u81F3\u5C11\u9694 20 \u79D2\uFF09\u3002" }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm a1-state-action", onClick: () => go({ tab: null, href: '#/accounts' }), "data-testid": "admin-overview-empty-pool-action", children: "\u53BB\u5E33\u865F\u6C60" })] })) : null, _jsx("div", { className: "a1-kpis", "data-testid": "admin-overview-kpis", children: kpis.map((kpi) => (_jsxs("button", { type: "button", className: "a1-kpi", "data-testid": "admin-overview-kpi-card", "data-kpi": kpi.key, "data-pending": kpi.pending ? 'true' : 'false', "data-live": kpi.live ? 'true' : 'false', title: `${kpi.label}：${kpi.hint}｜點一下切到「${goLabel(kpi)}」`, onClick: () => go(kpi), children: [_jsxs("span", { className: "a1-kpi-top", children: [_jsx("span", { className: "a1-kpi-label", children: kpi.label }), _jsx("span", { className: "a1-kpi-go", "aria-hidden": "true", children: `→ ${goLabel(kpi)}` })] }), _jsx("span", { className: `a1-kpi-value${kpi.pending ? ' is-pending' : ''}`, children: kpi.value }), _jsx("span", { className: "a1-kpi-hint", children: kpi.hint })] }, kpi.key))) }), _jsxs("div", { className: "a1-card", "data-testid": "admin-overview-trend", children: [_jsxs("div", { className: "a1-card-head", children: [_jsx("span", { className: "a1-card-title", children: "\u8FD1 7 \u5929\u8A3B\u518A\u7522\u51FA" }), _jsx("span", { className: "a1-card-meta", "data-testid": "admin-overview-today", children: !today
                                            ? '今天 —'
                                            : today.hasRun === false
                                                ? // 今天還沒跑：三個 0 會被讀成「跑了三批全失敗」，要給 —
                                                    '今天還沒跑（—）'
                                                : `今天 ek21 ${formatCount(today.ek21)} · UT ${formatCount(today.ut)} · 失敗 ${formatCount(today.failed)}` }), _jsx("span", { className: "a1-card-note", children: "\u6C92\u8DD1\u7684\u65E5\u5B50\u986F\u793A \u2014\uFF08\u4E0D\u662F 0\uFF09" })] }), trend.length === 0 ? (_jsx("p", { className: "a1-empty", "data-testid": "admin-overview-trend-empty", children: data
                                    ? '後端沒有註冊批次紀錄；先按一次「註冊一批」才會有數字。'
                                    : '管理資料暫時沒有接通，請稍後重新整理；接通後這裡會逐日列出 ek21／UT 的成功與失敗數。' })) : (trend.map((day) => (_jsxs("div", { className: `a1-day${day.empty ? ' is-empty' : ''}`, "data-testid": "admin-overview-trend-row", "data-date": day.date, "data-empty": day.empty ? 'true' : 'false', children: [_jsx("span", { className: "a1-day-date", children: day.label }), _jsx("span", { className: "a1-metrics", children: day.metrics.map((metric) => (_jsxs("span", { className: "a1-metric", "data-metric": metric.tone, "data-null": metric.missing ? 'true' : 'false', children: [_jsx("span", { className: "a1-metric-key", children: metric.label }), _jsx("span", { className: "a1-metric-bar", "aria-hidden": "true", children: metric.missing ? null : (_jsx("i", { className: `a1-bar-${metric.tone}`, style: { display: 'block', height: '100%', width: `${Math.round(metric.ratio * 100)}%` } })) }), _jsx("span", { className: "a1-metric-num", children: metric.value })] }, metric.label))) })] }, day.date))))] }), _jsxs("div", { className: "a1-card", "data-testid": "admin-overview-alerts", children: [_jsxs("div", { className: "a1-card-head", children: [_jsx("span", { className: "a1-card-title", children: "\u9700\u8981\u6CE8\u610F" }), _jsx("span", { className: "a1-card-meta", children: data ? `${alerts.items.length} 筆` : '—' }), _jsx("span", { className: "a1-card-note", children: "\u6700\u591A 5 \u7B46 \u00B7 \u9EDE\u4E00\u7B46\u8DF3\u5230\u5C0D\u61C9\u5206\u9801" })] }), alerts.items.length === 0 ? (_jsx("p", { className: "a1-empty", "data-testid": "admin-overview-alert-empty", children: data ? '目前沒有需要注意的事。' : '管理資料暫時沒有接通，請稍後重新整理。' })) : (_jsx("div", { className: "a1-alerts", children: alerts.items.map((alert, index) => (_jsxs("button", { type: "button", className: "a1-alert", "data-testid": "admin-overview-alert", "data-alert-code": alert.code, "data-alert-level": alert.level, title: `點一下切到「${goLabel(alert)}」`, onClick: () => go(alert), children: [_jsx("span", { className: alertDotClass(alert.level), "aria-hidden": "true" }), _jsxs("span", { className: "a1-alert-main", children: [_jsx("span", { className: `a1-alert-level${alert.level === 'danger' ? ' is-danger' : alert.level === 'warn' ? ' is-warn' : ''}`, children: alert.levelLabel }), _jsx("span", { className: "a1-alert-msg", children: alert.message })] }), _jsx("span", { className: "a1-alert-when", children: alert.when })] }, `${alert.code}-${index}`))) })), alerts.hidden > 0 ? (_jsxs("p", { className: "a1-foot", "data-testid": "admin-overview-alerts-more", children: ["\u53E6\u6709 ", alerts.hidden, " \u7B46\u672A\u986F\u793A\uFF08\u53EA\u5217\u524D 5 \u7B46\uFF09\u3002"] })) : null] }), _jsx("p", { className: "a1-foot", "data-testid": "admin-overview-foot", children: "\u8CC7\u6599\u4F86\u6E90\uFF1A\u7BA1\u7406\u5F8C\u7AEF\u5F59\u7E3D\uFF08/api/admin/overview \u5FEB\u7167\uFF09\uFF0C\u6309\u300C\u91CD\u65B0\u6574\u7406\u300D\u5373\u66F4\u65B0\uFF1B \u300C\u672A\u8B80\u7E3D\u6578\u300D\u4F8B\u5916\u2014\u2014\u5B83\u8DDF\u5DE6\u5074\u300C\u6536\u4EF6\u5323\u300D\u5FBD\u7AE0\u5403\u540C\u4E00\u4EFD\u5373\u6642\u6E05\u55AE\uFF0C\u6240\u4EE5\u5169\u500B\u6578\u5B57\u6C38\u9060\u4E00\u81F4\u3002 \u9EDE\u5361\u7247\u6216\u544A\u8B66\u53EA\u5207\u63DB\u5206\u9801\uFF0C\u4E0D\u6703\u53E6\u5916\u9001\u51FA\u8ACB\u6C42\u3002" })] })] }));
}
//# sourceMappingURL=OverviewPage.js.map